import { useState } from 'react';
import { Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core';
import type { EligibleInvestor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddInvestor/AddInvestorModal.types';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	eligibleInvestors: EligibleInvestor[];
	freeRows: number | null;
	totalRows: number | null;
	isSubmitting: boolean;
	onSubmit: (investorIds: string[]) => Promise<void>;
}

export const AddInvestorModal = ({
	isOpen,
	onClose,
	eligibleInvestors,
	freeRows,
	totalRows,
	isSubmitting,
	onSubmit,
}: Props) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const handleClose = () => {
		setSelectedIds([]);
		onClose();
	};

	const handleSubmit = async () => {
		await onSubmit(selectedIds);
		setSelectedIds([]);
	};

	return (
		<Modal.Root opened={isOpen} onClose={handleClose} centered>
			<Modal.Overlay />
			{/* data-testid goes on Content, not Root: Root has no box of its own, so a Playwright
			    visibility check against it never resolves even while the modal is genuinely open. */}
			<Modal.Content data-testid={TEST_ELEMENT_IDS.ADD_INVESTOR_MODAL}>
				<Modal.Header>
					<Modal.Title>Add investor</Modal.Title>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body>
					<Stack gap="sm">
						{freeRows !== null && totalRows !== null && (
							<Text
								size="sm"
								c="dimmed"
								data-testid={TEST_ELEMENT_IDS.ADD_INVESTOR_MODAL_SLOTS_COUNTER}
							>
								{freeRows} of {totalRows} slots free
							</Text>
						)}

						<Checkbox.Group value={selectedIds} onChange={setSelectedIds}>
							<Stack gap="xs">
								{eligibleInvestors.map((investor) => (
									<Checkbox
										key={investor.id}
										value={investor.id}
										label={investor.name}
										// data-testid on <Checkbox> lands on the bare <input>, which has no
										// text of its own; wrapperProps puts it on the root, alongside the
										// label. Stack stretches that root full-width, so shrink it back to
										// content — otherwise a centered click lands past the label.
										wrapperProps={{
											'data-testid':
												TEST_ELEMENT_IDS.ADD_INVESTOR_MODAL_OPTION,
											style: { width: 'fit-content' },
										}}
									/>
								))}
							</Stack>
						</Checkbox.Group>

						<Group justify="right" gap="xs">
							<Button variant="default" onClick={handleClose} disabled={isSubmitting}>
								Cancel
							</Button>
							<Button
								disabled={isSubmitting || selectedIds.length === 0}
								loading={isSubmitting}
								onClick={() => void handleSubmit()}
								data-testid={TEST_ELEMENT_IDS.ADD_INVESTOR_MODAL_SUBMIT_BUTTON}
							>
								Add investors
							</Button>
						</Group>
					</Stack>
				</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
};
